import { FC, useCallback, useEffect, useState, useRef } from 'react'
import { useRouter } from 'next/router'
import styles from './SearchForm.module.scss'
import { useTranslation } from 'next-i18next'

//component
import DateRangeCalendarComponent from 'components/calendars/DateRangeCalendarSimpleComponent'
import LuggageButtonComponent from 'components/buttons/LuggageButtonComponent'
import { Coordinate } from 'state/ducks/space/models'
import useOnclickOutside from 'react-cool-onclickoutside'
import { usePlaceAutoCompletion } from 'features/places/hooks/usePlaceAutoCompletion'
import { toAnalyticsLocation } from 'features/tracking/lib/location'
import { GTM_EVENT_DEFAULT_ADDRESS_SEARCH, sendGtmLocationEvent } from 'lib/gtag'

type Props = {
  name: string
  latitude: number
  longitude: number
}

const SearchForm: FC<Props> = ({ name, latitude, longitude }: Props) => {
  const router = useRouter()

  const { t } = useTranslation()

  const [selectedDate, setSelectedDate] = useState<{
    startDate: moment.Moment | null
    endDate: moment.Moment | null
  }>({ startDate: null, endDate: null })
  const [latLng, setLatLng] = useState<Coordinate>({
    lat: latitude,
    lon: longitude,
  })
  const [bagSizeStepper, setBagSizeStepper] = useState<number>(0)
  const [suitcaseSizeStepper, setSuitcaseSizeStepper] = useState<number>(0)
  const [isSearchButtonEnabled, setIsSearchButtonEnabled] = useState<boolean>(false)
  const [openLuggage, setOpenLuggage] = useState<boolean>(false)
  const [openSuggest, setOpenSuggest] = useState<boolean>(false)
  const [hasSelectedPlace, setHasSelectedPlace] = useState<boolean>(false)

  const luggageRef = useRef(null)

  const { places, status, setPlaces, setSearchText, setSearchTextFromInput, selectPlace } =
    usePlaceAutoCompletion()
  const [inputText, setInputText] = useState<string>('')

  const _onPressLuggageContainer = useCallback((event): void => {
    luggageRef.current.contains(event.target) ? setOpenLuggage(true) : setOpenLuggage(false)
  }, [])

  const _onPressNavigationSearchSpacePage = useCallback(
    (value, selectedDate, bagSize, suitcaseSize, lat, lon, isLocation?): void => {
      const startDate =
        selectedDate.startDate === null ? null : selectedDate.startDate.format('yyyy-MM-DD')
      const endDate =
        selectedDate.endDate === null ? null : selectedDate.endDate.format('yyyy-MM-DD')

      router.push({
        pathname: '/locations',
        query: {
          name: value,
          startDate: startDate,
          endDate: endDate,
          bagSize: bagSize,
          suitcaseSize: suitcaseSize,
          lat: lat,
          lon: lon,
          isLocation: isLocation,
        },
      })
    },
    []
  )

  /**
   * 初期化処理
   */
  useEffect(() => {
    //remove LuggageContainer on Clicked out side of dom
    document.addEventListener('click', _onPressLuggageContainer)

    return () => {
      document.removeEventListener('click', _onPressLuggageContainer)
    }
  }, [])

  /**
   * 初期化処理
   * name検知
   */
  useEffect(() => {
    // Initial set name if you have a name
    setInputText(name)
    setSearchText(name)
  }, [name, setSearchText])

  //update isSearchButtonEnabled
  useEffect(() => {
    const enabled =
      inputText === '' &&
      (selectedDate.startDate === null || selectedDate.endDate === null) &&
      bagSizeStepper === 0 &&
      suitcaseSizeStepper === 0
        ? false
        : true

    setIsSearchButtonEnabled(enabled)
  }, [selectedDate, bagSizeStepper, suitcaseSizeStepper, inputText])

  const handleInput = (e) => {
    const text = e.target.value
    setInputText(text)
    setSearchTextFromInput(text)

    // Processing of suggestion display control when page is displayed.
    if (!openSuggest) return setOpenSuggest(true)
  }

  const _handleSelect = useCallback(
    async (place) => {
      setInputText(place.description)
      setSearchText('')
      setPlaces([])

      const geocode = await selectPlace(place)

      if (geocode) {
        const { lat, lng } = geocode
        setLatLng({ lat: lat, lon: lng })
        setHasSelectedPlace(true)
      }
    },
    [selectPlace, setPlaces, setSearchText]
  )

  const _onPressSearchContainer = useOnclickOutside(() => {
    setPlaces([])
  })

  return (
    <div className={styles.search}>
      <div className={styles.searchContainer}>
        <div
          className={`${styles.searchInput} ${styles.searchInputArea}`}
          ref={_onPressSearchContainer}
        >
          <img
            className={styles.searchInputAreaIcon}
            src="/search_placeholder_icon.svg"
            alt={t('META.PLACEHOLDER_SEARCH_ICON')}
          />
          <div className={styles.searchInputFormWrap}>
            <input
              type="text"
              value={inputText}
              onChange={handleInput}
              placeholder={t('SPACES.SEARCH.AREA.PLACEHOLDER')}
              className={`${styles.searchInputForm} ${styles.searchInputAreaForm}`}
            />
            {openSuggest && (
              <>
                {status === 'OK' && (
                  <ul className={styles.suggest}>
                    {places.map((suggestion) => (
                      <li
                        key={suggestion.place_id}
                        onClick={() => _handleSelect(suggestion)}
                        className={styles.suggestText}
                      >
                        {suggestion.description}
                      </li>
                    ))}
                  </ul>
                )}
                {status === 'ZERO_RESULTS' && (
                  <div className={styles.noResult}>
                    <p className={styles.noResultText}>{t('SPACES.SEARCH.AREA.NO_RESULT')}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className={styles.searchInputDateSizeCount}>
          <div className={`${styles.searchInput} ${styles.searchInputDate}`}>
            <img
              className={styles.searchInputDateImage}
              src="/calendar_placeholder_icon.svg"
              alt={t('META.PLACEHOLDER_CALENDAR_ICON')}
            />
            <div className={styles.searchInputFormWrap}>
              <DateRangeCalendarComponent
                onPressCloseModalRequest={() => {}}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              />
            </div>
          </div>
          <div
            className={`${styles.searchInput} ${styles.searchInputSizeCount}`}
            onClick={_onPressLuggageContainer}
            ref={luggageRef}
          >
            <div className={styles.searchInputFormWrap}>
              <span className={styles.searchInputSizeCountSuitcase}>
                <img
                  className={styles.searchInputSizeCountSuitcaseImage}
                  src="/suitcase_icon_gray_no_margin.svg"
                  alt={t('META.PLACEHOLDER_LUGGAGE_ICON')}
                />
                {`x ${suitcaseSizeStepper}`}
              </span>
              <span className={styles.searchInputSizeCountBag}>
                <img
                  className={styles.searchInputSizeCountBagImage}
                  src="/bag_icon_gray.svg"
                  alt={t('META.PLACEHOLDER_LUGGAGE_ICON')}
                />
                {`x ${bagSizeStepper}`}
              </span>
            </div>
            {openLuggage && (
              <div className={styles.luggageContainer}>
                <div className={styles.luggageContainerBagSize}>
                  <div className={styles.counter}>
                    <div className={styles.counterLabel}>{t('COMMON.BAG_SIZE')}</div>
                    <LuggageButtonComponent
                      sizeStepper={bagSizeStepper}
                      setSizeStepper={setBagSizeStepper}
                    />
                  </div>
                  <div className={styles.description}>
                    {t('SPACES.SEARCH.LUGGAGE.BAG_SIZE_DESCRIPTION')}
                  </div>
                </div>
                <div className={styles.luggageContainerSuitecaseSize}>
                  <div className={styles.counter}>
                    <div className={styles.counterLabel}>{t('COMMON.SUITECASE_SIZE')}</div>
                    <LuggageButtonComponent
                      sizeStepper={suitcaseSizeStepper}
                      setSizeStepper={setSuitcaseSizeStepper}
                    />
                  </div>
                  <div className={styles.description}>
                    {t('SPACES.SEARCH.LUGGAGE.SUITECASE_SIZE_DESCRIPTION')}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.searchButtonWrap}>
        <button
          className={`${styles.searchButton} ${
            isSearchButtonEnabled ? '' : styles.searchButtonDisabled
          }`}
          onClick={() => {
            if (!isSearchButtonEnabled) {
              return
            }

            const lat = latLng.lat || latitude
            const lon = latLng.lon || longitude

            // 候補を選ばずにエリアページの地点のまま検索した場合は、アプリのプリセットエリア選択と同じイベントを送る
            if (!hasSelectedPlace) {
              sendGtmLocationEvent(GTM_EVENT_DEFAULT_ADDRESS_SEARCH, {
                search_keyword: name,
                ...toAnalyticsLocation(Number(lat), Number(lon)),
              })
            }

            _onPressNavigationSearchSpacePage(
              inputText || name,
              selectedDate,
              bagSizeStepper,
              suitcaseSizeStepper,
              lat,
              lon
            )
          }}
        >
          {t('AREAS.CITY.SLUG.SEARCH_SEE_NEARBY_BUTTON')}
        </button>
      </div>
    </div>
  )
}

export default SearchForm
